import { Compass, Network, Mail, Server, ArrowUpRight } from "lucide-react";
import { Reveal, ChapterTag } from "@/components/Reveal";
import { useLang } from "@/i18n";

const meta = {
  consulting: {
    icon: Compass,
    span: "lg:col-span-7",
  },
  networking: {
    icon: Network,
    span: "lg:col-span-5",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxkYXRhJTIwY2VudGVyJTIwc2VydmVyJTIwcm9vbSUyMGRhcmt8ZW58MHx8fHwxNzg2NDg0NzU4fDA&ixlib=rb-4.1.0&q=85",
  },
  email: {
    icon: Mail,
    span: "lg:col-span-5",
  },
  hosting: {
    icon: Server,
    span: "lg:col-span-7",
    image: "https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwY2FibGVzJTIwZ2xvd2luZ3xlbnwwfHx8fDE3ODY0ODQ3NTB8MA&ixlib=rb-4.1.0&q=85",
  },
};

export default function Services() {
  const { t } = useLang();
  const services = t.services.items.map((s) => ({ ...s, ...meta[s.id] }));
  return (
    <section id="services" className="relative py-28 lg:py-40" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <ChapterTag index="01" label={t.services.tag} />
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-white mt-6 max-w-3xl" data-testid="services-heading">
            {t.services.heading}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className={s.span}>
              <article
                data-testid={`service-card-${s.id}`}
                className="group relative h-full min-h-[320px] border border-white/10 rounded-xl overflow-hidden bg-[#0A0A0F] p-8 lg:p-10 flex flex-col justify-between transition-[border-color,transform] duration-500 hover:border-brand/60 hover:-translate-y-1"
              >
                {s.image && (
                  <>
                    <img src={s.image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-neutral-500">[ 0{i + 1} ]</span>
                  <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-brand-light group-hover:bg-brand group-hover:text-white group-hover:border-brand transition-[background-color,color,border-color] duration-500">
                    <s.icon size={20} strokeWidth={1.5} />
                  </span>
                </div>
                <div className="relative mt-16">
                  <h3 className="font-display text-2xl lg:text-3xl font-bold text-white tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-neutral-400 leading-relaxed max-w-md">{s.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 border border-white/10 rounded-full px-3 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                  <a href="#contact" data-testid={`service-quote-${s.id}`} className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-light hover:text-white transition-colors duration-300">
                    {t.services.quote}
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
